import { apiJson } from '../api';
import { detectedTimezone } from '../utils/readerEmail';

export interface ReaderEmail {
  active: boolean;
  cadence: string;
  article_count: number;
  send_hour: number;
  timezone: string;
  /** False until the reader has saved settings, so the client can detect the zone. */
  configured?: boolean;
  /** False until the reader proves they own the address; delivery cannot be turned on before that. */
  email_verified: boolean;
}

export function getReaderEmail(): Promise<ReaderEmail> { return apiJson('/api/reader/email'); }
export function setReaderEmail(settings: Partial<ReaderEmail> & { active: boolean }): Promise<ReaderEmail> {
  return apiJson('/api/reader/email', { method: 'PUT', json: settings });
}
/** Email the account's address a link that proves the reader owns it. */
export function requestEmailVerification(): Promise<{ email_verified: boolean; sent: boolean }> {
  return apiJson('/api/reader/email/verification', { method: 'POST' });
}
/** Spend a confirmation link's token. Needs no session: the link may open in another browser. */
export function verifyEmail(token: string): Promise<{ email_verified: boolean }> {
  return apiJson('/api/reader/email/verify', { method: 'POST', json: { token }, public: true });
}
/**
 * Turn on the daily brief email, keeping the reader's send hour and zone.
 * Only ever subscribes: an unchecked "Email me my daily brief" box leaves an
 * existing subscription alone; account settings are the unsubscribe surface.
 *
 * An address the reader has not proven yet cannot receive it, so that case
 * sends the confirmation link instead and resolves 'verification_sent'.
 */
export async function ensureDailyBriefSubscription(): Promise<'subscribed' | 'verification_sent'> {
  const settings = await getReaderEmail();
  if (!settings.email_verified) {
    await requestEmailVerification();
    return 'verification_sent';
  }
  await setReaderEmail({
    active: true,
    cadence: 'daily',
    send_hour: settings.send_hour,
    timezone: settings.timezone || detectedTimezone(),
  });
  return 'subscribed';
}
export function getReaderExports(): Promise<{ rss: string; markdown: string }> {
  return apiJson('/api/reader/export-links');
}
